import { FC, useMemo } from "react";
import {
  Backdrop,
  Button,
  Card,
  CircularProgress,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../../locales/tokens";
import { getStyles } from "./styles";
import { TimePicker } from "../../../../../ui-kit/timePicker";
import AddIcon from "@mui/icons-material/Add";
import { QuestionModal } from "../../testCreate/ui/components/questionModal";
import { TextInput } from "../../testCreate/ui/components/textInput";
import { BadgeInput } from "../../testCreate/ui/components/badgeInput";
import { AutocompleteSingle } from "../../testCreate/ui/components/autocompleteSingle/index.";
import { AutocompleteMultiline } from "../../testCreate/ui/components/autocompleteMultiline/index.";
import { CheckBox } from "../../testCreate/ui/components/checkBox";
import { QuestionList } from "../../testCreate/ui/components/questionList";
import { useTestEdit } from "../presenters/useTestEdit";
import { CheckboxDepartmentAutocompleteMultiple } from "../../../../../ui-kit/checkboxDepartmentAutocompleteMultiple";
import { SelectCheckBox } from "../../../../../ui-kit/selectCheckbox";
import { paths } from "../../../../../paths";
import { Link } from "react-router-dom";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { AutocompleteCheckBox } from "../../../../../ui-kit/autocompleteCheckBox";

export const TestEdit: FC = () => {
  const { t } = useTranslation();
  const lgDown = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));
  const styles = useMemo(() => getStyles(lgDown), [lgDown]);
  const {
    theme,
    themeValid,
    onChangeTheme,
    orgStructure,
    groupPermissions,
    groupPermissionsValid,
    onChangePermissions,
    receiver,
    onChangeReceiver,
    initiator,
    initiatorValid,
    onChangeInitiator,
    onSelectInitiator,
    publishChecked,
    handlePublishChange,
    notifyChecked,
    handleNotifyChange,
    editTest,
    cancelCreate,
    editClick,
    deadline,
    handleChangeDeadline,
    deadlineValid,
    attemptAmount,
    attemptAmountValid,
    onChangeAttemptAmount,
    onChangePoints,
    points,
    onChangeMinPoints,
    minPoints,
    minPointsValid,
    content,
    onChangeContent,
    badge,
    handleChangeBadge,
    badgesList,
    changeBadgeText,
    handleOpenQuestionModal,
    isQuestionModalOpen,
    questionList,
    removeQuestion,
    onDragEnd,
    defineCurrentQuestion,
    currentQuestion,
    handleCloseQuestionModal,
    id,
    isDeleted,
    contentValid,
    onChangeDepartmentPermissions,
    departmentPermissions,
    handleChangePosition,
    positions,
    positionsList,
    onChangeAssignDepartment,
    assignDepartment,
    handleChangeAssignPosition,
    assignPositions,
    onChangeDuration,
    duration,
  } = useTestEdit();

  return (
    <Stack mt={2} ml={2}>
      <Typography variant="h4" fontWeight={600}>
        {t(tokens.test.editTest)}
      </Typography>
      <Stack my={5} spacing={3}>
        <Stack
          mt={3}
          direction="row"
          justifyContent="space-between"
          spacing={2}
        >
          <Stack width="49.2%">
            <TextInput
              title={t(tokens.test.theme) + "*"}
              label={t(tokens.test.theme)}
              isValid={themeValid}
              value={theme}
              isMultiline={false}
              onChangeValue={onChangeTheme}
              isDisabled={isDeleted}
            />
          </Stack>
          <Stack width="49%">
            <AutocompleteSingle
              testId={isDeleted}
              isValid={initiatorValid}
              title={t(tokens.test.testInitiator) + "*"}
              label={t(tokens.test.testInitiator)}
              value={initiator}
              orgStructure={orgStructure}
              onChangeInitiator={onChangeInitiator}
              onSelectInitiator={onSelectInitiator}
            />
          </Stack>
        </Stack>
        <Stack
          mt={3}
          direction="row"
          justifyContent="space-between"
          spacing={2}
        >
          <Stack width="100%" minWidth="49%">
            <TextInput
              title={t(tokens.test.shortlyAboutTest)}
              label={t(tokens.test.shortlyAboutTest)}
              isValid={contentValid}
              value={content}
              isMultiline={true}
              onChangeValue={onChangeContent}
              isDisabled={isDeleted}
            />
          </Stack>
          <Stack width="100%" minWidth="50%" spacing={2}>
            <TextInput
              title={t(tokens.test.attemptsAmount) + "*"}
              label={t(tokens.test.attemptsAmount)}
              isValid={attemptAmountValid}
              value={attemptAmount}
              isMultiline={false}
              onChangeValue={onChangeAttemptAmount}
              isDisabled={isDeleted}
            />
            <TextInput
              title={t(tokens.test.minPoints) + "*"}
              label={t(tokens.test.minPoints)}
              isValid={minPointsValid}
              value={minPoints}
              isMultiline={false}
              onChangeValue={onChangeMinPoints}
              isDisabled={isDeleted}
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Stack width="100%">
            <BadgeInput
              title={t(tokens.test.badge)}
              value={badge}
              bagesList={badgesList}
              handleChangeBadge={handleChangeBadge}
              changeBadgeText={changeBadgeText}
              isDisabled={isDeleted}
            />
          </Stack>
          <TextInput
            title={t(tokens.test.points)}
            label={t(tokens.test.points)}
            isValid={true}
            value={points}
            isMultiline={false}
            onChangeValue={onChangePoints}
            isDisabled={isDeleted}
          />
        </Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={2}
          pb={1}
        >
          <Stack width="100%" spacing={0.5}>
            <TextInput
              title={t(tokens.test.duration)}
              label={t(tokens.test.duration)}
              isValid={true}
              value={duration}
              isMultiline={false}
              onChangeValue={onChangeDuration}
              isDisabled={isDeleted}
            />
            <Typography color="#F1594E" variant="caption">
              (для департаментів та посад)
            </Typography>
          </Stack>
          <Stack width="100%">
            <Typography
              variant="h6"
              mb={1}
              color={deadlineValid ? "inherit" : "error"}
            >
              {t(tokens.test.deadline)}
            </Typography>
            <Stack spacing={0.5}>
              <TimePicker
                isDisabled={isDeleted}
                value={deadline}
                handleDate={handleChangeDeadline}
              />
              <Typography color="#F1594E" variant="caption">
                (для користувачів)
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Card elevation={3} sx={styles.card}>
          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={2}
            mb={1}
          >
            <AutocompleteMultiline
              isDisabled={isDeleted}
              isValid={groupPermissionsValid}
              title={t(tokens.documents.accessToUser)}
              label={t(tokens.documents.accessToUser)}
              value={groupPermissions}
              orgStructure={orgStructure}
              onChangePermissions={onChangePermissions}
              withSelectAll
            />
            <Stack width="100%" minWidth="49%">
              <Typography variant="h6" mb={1}>
                {t(tokens.documents.accessToDepartment)}
              </Typography>
              <CheckboxDepartmentAutocompleteMultiple
                title={t(tokens.documents.accessToDepartment)}
                value={departmentPermissions}
                optionList={orgStructure}
                onChange={onChangeDepartmentPermissions}
                isDisabled={isDeleted}
                withSelectAll
              />
            </Stack>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={2}
            mb={1}
          >
            <Stack width="49.2%" flexShrink={0}>
              <Typography variant="h6" mb={1}>
                {t(tokens.documents.accessToPosition)}
              </Typography>
              <AutocompleteCheckBox
                positionsList={positionsList}
                value={positions}
                handleChange={handleChangePosition}
                isDisabled={isDeleted}
                placeholder="Доступ за посадаю"
                withSelectAll
              />
            </Stack>
            <Stack width="100%" pt={5}>
              <Button
                variant="contained"
                sx={styles.editBtn}
                disabled={editClick || isDeleted}
                startIcon={<EditOutlinedIcon />}
                component={Link}
                to={paths.dashboard.content.test.editAccess.replace(
                  ":id",
                  String(id)
                )}
              >
                {t(tokens.common.editAccess)}
              </Button>
            </Stack>
          </Stack>
        </Card>
        <Card elevation={3} sx={styles.card}>
          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={2}
            mb={1}
          >
            <Stack width="49.2%">
              <AutocompleteMultiline
                testId={null}
                isValid={true}
                title={t(tokens.documents.assignToUser)}
                label={t(tokens.documents.assignToUser)}
                value={receiver}
                orgStructure={orgStructure}
                onChangePermissions={onChangeReceiver}
                isDisabled={isDeleted}
                withSelectAll
              />
            </Stack>
            <Stack width="49%">
              <Typography variant="h6" mb={1}>
                {t(tokens.documents.assignToDepartment)}
              </Typography>
              <CheckboxDepartmentAutocompleteMultiple
                title={t(tokens.documents.assignToDepartment)}
                value={assignDepartment}
                optionList={orgStructure}
                onChange={onChangeAssignDepartment}
                isDisabled={isDeleted}
                withSelectAll
              />
            </Stack>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={2}
            mb={1}
          >
            <Stack width="49.2%" flexShrink={0}>
              <Typography variant="h6" mb={1}>
                {t(tokens.documents.assignToPosition)}
              </Typography>
              <AutocompleteCheckBox
                positionsList={positionsList}
                value={assignPositions}
                handleChange={handleChangeAssignPosition}
                isDisabled={isDeleted}
                placeholder="Доступ за посадаю"
                withSelectAll
              />
            </Stack>
            <Stack width="100%" pt={5}>
              <Button
                variant="contained"
                sx={styles.editBtn}
                disabled={editClick || isDeleted}
                startIcon={<EditOutlinedIcon />}
                component={Link}
                to={paths.dashboard.content.test.editAssign.replace(
                  ":id",
                  String(id)
                )}
              >
                {t(tokens.common.editAssign)}
              </Button>
            </Stack>
          </Stack>
        </Card>
        <Stack direction="row" alignItems="center" pt={2}>
          <Button
            startIcon={<AddIcon />}
            sx={styles.addQuestionButton}
            onClick={handleOpenQuestionModal}
            disabled={isDeleted}
          >
            {t(tokens.test.addQuestion)}
          </Button>
        </Stack>
        {questionList.length > 0 && (
          <Stack mt={2} spacing={1} px={2}>
            <QuestionList
              isDisabled={isDeleted}
              questions={questionList}
              onDragEnd={onDragEnd}
              removeQuestion={removeQuestion}
              defineCurrentQuestion={defineCurrentQuestion}
            />
          </Stack>
        )}
        <Stack direction="row" mt={3} spacing={3}>
          <CheckBox
            testId={id}
            isPublish={true}
            title={t(tokens.documents.publish)}
            value={publishChecked}
            onChangeValue={handlePublishChange}
            isDisabled={isDeleted}
          />
          <CheckBox
            testId={null}
            title={t(tokens.documents.inform)}
            value={notifyChecked}
            onChangeValue={handleNotifyChange}
            isDisabled={isDeleted}
          />
        </Stack>
        {!isDeleted && (
          <Stack direction="row" mt={3} spacing={3}>
            <Button
              variant="contained"
              sx={styles.createButton}
              onClick={editTest}
              disabled={editClick}
            >
              {t(tokens.common.save)}
            </Button>
            <Button
              variant="outlined"
              sx={styles.cancelButton}
              onClick={cancelCreate}
              disabled={editClick}
            >
              {t(tokens.common.cancel)}
            </Button>
          </Stack>
        )}
      </Stack>
      {/* <Backdrop sx={styles.backDrop} open={editClick}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <CircularProgress color="primary" />
                    <Typography color="primary" variant="h6">
                        {t(tokens.documents.await)}
                    </Typography>
                </Stack>
            </Backdrop> */}
      <QuestionModal
        testId={id}
        isOpen={isQuestionModalOpen}
        currentQuestion={currentQuestion}
        onClose={handleCloseQuestionModal}
      />
    </Stack>
  );
};
